import React from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { Home, BookOpen, Briefcase, MessageCircle, User, LogOut, Shield, Package, CalendarClock, HelpCircle, BookMarked, Users, PlayCircle, Mail, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { LogoLockup } from "@/components/Logo";
import { Avatar } from "@/components/Avatar";
import NotificationBell from "@/components/NotificationBell";
import WelcomePopup from "@/components/WelcomePopup";
import PushPrompt from "@/components/PushPrompt";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/", label: "Home", icon: Home, testId: "nav-home" },
  { to: "/course", label: "Free Course", icon: BookOpen, testId: "nav-free-course" },
  { to: "/products", label: "Upskill", icon: Package, testId: "nav-upskill" },
  { to: "/jobs", label: "Jobs", icon: Briefcase, testId: "nav-jobs" },
  { to: "/ai-proposal", label: "AI Proposal", icon: Sparkles, testId: "nav-ai" },
  { to: "/support", label: "Support", icon: MessageCircle, testId: "nav-support" },
  { to: "/faqs", label: "FAQ", icon: HelpCircle, testId: "nav-faqs" },
];
const MOBILE_NAV = [
  { to: "/", label: "Home", icon: Home, testId: "nav-home" },
  { to: "/course", label: "Course", icon: BookOpen, testId: "nav-free-course" },
  { to: "/jobs", label: "Jobs", icon: Briefcase, testId: "nav-jobs" },
  { to: "/support", label: "Support", icon: MessageCircle, testId: "nav-support" },
  { to: "/profile", label: "Profile", icon: User, testId: "nav-profile" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const active = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));
  React.useEffect(() => { document.documentElement.dataset.theme = localStorage.getItem("cr_theme") || ""; }, []);

  // Session engagement tracking — heartbeat every 45s, tagged course vs other
  const pathRef = React.useRef(location.pathname);
  React.useEffect(() => { pathRef.current = location.pathname; }, [location.pathname]);
  React.useEffect(() => {
    if (!user || user.role !== "student") return;
    const beat = () => {
      if (document.hidden) return;
      const p = pathRef.current;
      const section = (p.startsWith("/course") || p.startsWith("/lesson") || p.startsWith("/phase")) ? "course" : "other";
      api.post("/analytics/heartbeat", { section, seconds: 45 }).catch(() => {});
    };
    const id = setInterval(beat, 45000);
    return () => clearInterval(id);
  }, [user]);

  const isStaff = user && (user.role === "admin" || user.role === "reviewer" || user.role === "support");

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0 flex flex-col">
      <header className="sticky top-0 z-40 bg-white border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-[72px] flex items-center justify-between gap-4">
          <Link to="/" className="shrink-0 flex items-center gap-2.5" data-testid="logo-link">
            <img src="/logo-icon.png" alt="Coach Reyland" className="h-9 w-9 rounded-[22%] object-contain ring-1 ring-black/5" />
            <span className="hidden sm:flex flex-col leading-none">
              <span className="font-heading font-extrabold text-navy text-lg">Coach Reyland</span>
              <span className="text-[9px] tracking-[0.22em] text-teal font-semibold mt-0.5">VA LEARNING PLATFORM</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={n.testId}
                className={cn("px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200",
                  active(n.to) ? "bg-teal text-white shadow-sm shadow-teal/40" : "text-navy/70 hover:text-navy hover:bg-navy/5")}>
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {user && <NotificationBell />}
            {isStaff && (
              <Button variant="outline" size="sm" className="rounded-full hidden sm:flex border border-navy/20 text-navy hover:bg-navy/5 font-semibold" data-testid="admin-panel-btn"
                onClick={() => navigate("/admin")}>
                <Shield className="w-4 h-4 mr-1.5" /> Admin
              </Button>
            )}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="rounded-full shadow-sm overflow-hidden ring-2 ring-transparent hover:ring-teal/30 transition-all" data-testid="user-menu-btn">
                    <Avatar src={user.photo_url} name={user.name} size={40} testId="header-avatar" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5 text-sm">
                    <p className="font-medium truncate">{user.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                    <User className="w-4 h-4 mr-2" /> Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/inbox")} data-testid="menu-inbox">
                    <Mail className="w-4 h-4 mr-2" /> My Messages
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/products")} data-testid="menu-products">
                    <Package className="w-4 h-4 mr-2" /> Niche Courses
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/video")} data-testid="menu-video">
                    <PlayCircle className="w-4 h-4 mr-2" /> Video Walkthrough
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/mentorship")} data-testid="menu-mentorship">
                    <CalendarClock className="w-4 h-4 mr-2" /> 1-on-1 Mentorship
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/my-story")} data-testid="menu-story">
                    <BookMarked className="w-4 h-4 mr-2" /> My Story
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/faqs")} data-testid="menu-faqs">
                    <HelpCircle className="w-4 h-4 mr-2" /> FAQs
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/join-us")} data-testid="menu-join">
                    <Users className="w-4 h-4 mr-2" /> Join Our Team
                  </DropdownMenuItem>
                  {isStaff && (
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                      <Shield className="w-4 h-4 mr-2" /> Admin Panel
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button variant="outline" size="sm" className="rounded-full border border-navy/20 text-navy hover:bg-navy/5 font-semibold hidden sm:flex" onClick={() => navigate("/register")} data-testid="register-btn">Create an Account</Button>
                <Button size="sm" className="rounded-full bg-teal hover:bg-teal/90 text-white font-semibold shadow-sm shadow-teal/40" onClick={() => navigate("/login")} data-testid="login-btn">Log In</Button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 relative max-w-7xl w-full mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="decor-blob decor-blob-teal w-[420px] h-[420px] -top-32 -left-40 hidden sm:block" aria-hidden />
        <div className="decor-blob decor-blob-green w-[360px] h-[360px] top-[40%] -right-40 hidden lg:block" aria-hidden />
        <div className="relative z-10"><Outlet /></div>
      </main>

      <WelcomePopup />

      {/* Footer */}
      <footer className="border-t border-emerald-100 bg-white relative z-10" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid gap-8 md:grid-cols-3 items-center">
          <div>
            <Link to="/" data-testid="footer-logo"><LogoLockup /></Link>
            <p className="text-sm text-muted-foreground mt-3 max-w-xs">Real skills, real clients, a brighter you. Learn, apply, get hired, and grow as a Virtual Assistant.</p>
          </div>
          <nav className="flex flex-wrap gap-x-6 gap-y-2 md:justify-center">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} className="text-sm font-medium text-navy/70 hover:text-teal transition-colors" data-testid={`footer-${n.testId}`}>{n.label}</Link>
            ))}
          </nav>
          <p className="text-xs text-muted-foreground md:text-right">© {new Date().getFullYear()} Coach Reyland VA Learning Platform.<br className="hidden md:block" /> All rights reserved.</p>
        </div>
      </footer>

      {/* Floating support bubble */}
      <button onClick={() => navigate("/support")} data-testid="floating-support"
        className="fixed right-4 bottom-24 md:bottom-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-teal to-brandgreen text-white shadow-lg flex items-center justify-center hover:scale-105 transition-transform"
        title="Need help? Contact support">
        <MessageCircle className="w-6 h-6" />
      </button>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-emerald-100" data-testid="bottom-nav">
        <div className="grid grid-cols-5">
          {MOBILE_NAV.map((n) => {
            const Icon = n.icon;
            return (
              <Link key={n.to} to={n.to} data-testid={`bottom-${n.testId}`}
                className={cn("flex flex-col items-center justify-center py-2.5 gap-0.5 text-[11px] font-medium transition-colors",
                  active(n.to) ? "text-teal" : "text-muted-foreground")}>
                <Icon className="w-5 h-5" />
                {n.label}
              </Link>
            );
          })}
        </div>
      </nav>
      {user && <PushPrompt />}
    </div>
  );
}
